import _max from "lodash/max";
/**
 * @module
 *
 * @description Utils for working with date objects.
 *
 */
import "core-js/modules/es6.array.filter.js";
import "core-js/modules/es6.array.map.js";
import add from 'date-fns/add';
import format from 'date-fns/format';
import getDate from 'date-fns/getDate';
import toDate from 'date-fns/toDate';
import isValid from 'date-fns/isValid';
import { printDeprecationWarning } from '@nerdwallet/deprecate';

// eslint-disable-next-line import/no-cycle -- directive added automatically by Shepherd migration
import { getDate as getWpDate, getDateCustomLastModified, getDateHide, getDateModified, getDateShowLastModified, getDateUseCustomLastModified } from '../selectors/common';
export var PRETTY_DATE_DEFAULT_OPTIONS = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'America/Los_Angeles'
};

/**
 * Long localized date format token for date-fns
 * @see {@link https://date-fns.org/v2.9.0/docs/format#description}
 */
export var PRETTY_DATE_FORMAT = 'PP';

/**
 * Output of unsupported browsers.
 */
export var INVALID_DATE = 'Invalid Date';

/**
 * Return the current month and year for date title in Roundups
 * @note advances month & year on the 27th of the previous month
 *
 * @param   {Date} currentDate    date to test, will fallback to new Date()
 * @return  {string}              "Month YYYY"
 */
export var getRoundupMonthAndYear = function getRoundupMonthAndYear(date) {
  var SWITCH_DATE = 27;
  var currentDate = date || new Date();
  if (!isDateValid(currentDate)) {
    return null;
  }
  var nextMonth = add(currentDate, {
    months: 1
  });
  var formatting = 'MMMM yyyy';
  var shouldAdvance = getDate(currentDate) >= SWITCH_DATE;
  return shouldAdvance ? getDateFormatted(nextMonth, formatting) : getDateFormatted(currentDate, formatting);
};

/**
 * Return a formatted date based on provided options (or defaults).
 * @deprecated v2.5.1 - use `getDateFormatted` instead to ensure IE11 support
 *
 * @param {string} timestamp date in ISO-8601 format.
 * @param {Object} options formatting options passed to toLocaleDateString().
 *
 * @returns {string} formatted locale date string. Defaults to
 * "Month day, year" eg. "December 3, 2018".
 */
export var getDatePretty = function getDatePretty(timestamp) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : PRETTY_DATE_DEFAULT_OPTIONS;
  return timestamp && new Date(timestamp).toLocaleDateString('en-US', options);
};

/**
 * Return a formatted date based on provided token string (or defaults).
 *
 * @param {Object<Date>|number} date
 * @param {String} formatting tokens according to date-fns API.
 *
 * @returns {string} formatted locale date string. Defaults to
 * "Month day, year" eg. "December 3, 2018".
 */
export var getDateFormatted = function getDateFormatted(date) {
  var formatString = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : PRETTY_DATE_FORMAT;
  return date && format(toDate(date), formatString);
};

/**
 * Consider flags on the WP entity to determine the correct date
 * to return. Eg. if should show the last modified, use custom last modified, etc.
 * Default to the published date.
 *
 * @param {Object} entity WordPress entity.
 * @param {Object} format  formatting options passed to getDatePretty.
 *
 * @returns {string} formatted locale date string. Defaults to
 * "Month day, year" eg. "December 3, 2018".
 */
export var getDateCalculated = function getDateCalculated(entity, formatting) {
  return getDatePretty(getDateCalculatedIso(entity, true), formatting);
};

/**
 * Consider flags on the WP entity to determine the correct date
 * to return. Eg. if should show the last modified, use custom last modified, etc.
 * Default to the published date.
 *
 * _Note: Behavior with `withTimezone === false` deprecated and to be removed in 3.0_
 *
 * @param {Object} entity WordPress entity.
 * @param   {boolean} withTimezone     if true, returns timestamp with timezone info.
 *
 * @returns {string} date string in ISO-8601 format
 */
export var getDateCalculatedIso = function getDateCalculatedIso(entity, withTimezone) {
  if (!withTimezone) printDeprecatedTimezoneWarning('getDateCalculatedIso');
  var customLastModifiedDate = getDateCustomLastModified(entity, withTimezone);
  var hideDate = getDateHide(entity);
  var modifiedDate = getDateModified(entity, withTimezone);
  var publishedDate = getWpDate(entity, withTimezone);
  var showLastModifiedDate = getDateShowLastModified(entity);
  var useCustomLastModifiedDate = getDateUseCustomLastModified(entity);
  if (hideDate) {
    return false;
  }
  if (showLastModifiedDate) {
    // Default to published date if custom doesn't exist
    if (useCustomLastModifiedDate) {
      return customLastModifiedDate || publishedDate;
    }
    return modifiedDate || publishedDate;
  }
  return publishedDate;
};

/**
 * Provided an array of `Date` parseable strings, determine which is most recent
 * and return it in a prettified format.
 *
 * @param {Array} of `Date.parse()`-able dates
 * @param {Object} formatting  formatting options passed to getDatePretty.
 *
 * @returns {string} most recent from timestamp ingested list in ISO-8601 format
 */
export var getDateMostRecent = function getDateMostRecent(dates, formatting) {
  return getDatePretty(getDateMostRecentIso(dates), formatting);
};

/**
 * Provided an array of `Date` parseable strings, determine which is most recent
 * and return it in ISO-8601 format.
 *
 * @param {Array} of `Date.parse()`-able dates
 *
 * @returns {string} most recent from timestamp ingested list in ISO-8601 format
 */
export var getDateMostRecentIso = function getDateMostRecentIso() {
  var dates = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var filteredDateObjects = dates.filter(Boolean) // Date constructor coerces false into January 1st, 1970
  .map(function (date) {
    return new Date(date);
  }).filter(function (date) {
    return isDateValid(date);
  });
  var mostRecent = _max(filteredDateObjects);
  return mostRecent ? mostRecent.toISOString() : null;
};

/**
 * Determine whether a date is valid.
 *
 * @param {Object} `Date` object
 *
 * @returns {boolean} whether the date is valid
 */
export var isDateValid = function isDateValid(date) {
  return isValid(date);
};

/**
 * Appends a Z to the end of a string. Used to add the Z timezone to ISO8601 dates,
 * but only if it's not blank
 *
 * @param {string} str
 *
 * @private
 */
export var withZ = function withZ(str) {
  return str ? "".concat(str, "Z") : str;
};

/**
 * Shortcut function for outputting deprecated withTimezone message
 *
 * @param {string} fnName name of function
 *
 * @private
 */
export var printDeprecatedTimezoneWarning = function printDeprecatedTimezoneWarning(fnName) {
  return printDeprecationWarning({
    message: "Support for ".concat(fnName, " with `withTimezone === false` is deprecated and will be removed in the next major version See https://github.com/NerdWallet/nwjs-wp-api/pull/56#discussion_r329720572")
  });
};